import { Loader2, type LucideIcon } from 'lucide-react';
import type * as React from 'react';
import { cn } from '@/utils/style';
import { Tooltip } from './Tooltip';

interface ButtonProps extends React.ComponentProps<'button'> {
  icon?: LucideIcon;
  variant?: 'default' | 'outline' | 'color' | 'dashed';
  color?: 'purple' | 'green' | 'red' | 'black' | 'orange' | 'blue';
  isPending?: boolean;
  /** Disables the button and tells why on hover. */
  disabledReason?: string | null;
}

export function Button(props: ButtonProps) {
  const {
    className,
    icon: Icon,
    children,
    variant = 'default',
    color,
    isPending,
    disabled,
    disabledReason,
    type = 'button',
    ...rest
  } = props;

  const button = (
    <button
      type={type}
      data-slot="button"
      className={cn(
        "flex text-center items-center justify-center gap-1 cursor-pointer whitespace-nowrap rounded-lg text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover:opacity-75",
        variant === 'outline' && 'border border-gray-300 text-gray-700 p-2 py-2',
        variant === 'outline' &&
          color === 'red' &&
          'border-red-300 text-red-600 [&_svg]:text-red-600',
        variant === 'dashed' && 'border border-dashed border-gray-300',
        variant === 'color' && 'text-white px-4 py-2',
        (color === 'purple' || (variant === 'color' && color === undefined)) &&
          'bg-magenta-700 hover:bg-magenta-700/80',
        color === 'green' && 'bg-green-600 hover:bg-green-600/80',
        variant === 'color' && color === 'red' && 'bg-red-600 hover:bg-red-600/80',
        color === 'black' && 'bg-gray-900 hover:bg-gray-900/80',
        color === 'orange' && 'bg-orange-600 hover:bg-orange-600/80',
        color === 'blue' && 'bg-blue-600 hover:bg-blue-600/80',
        className
      )}
      {...rest}
      disabled={disabled || isPending || !!disabledReason}
    >
      {Icon && <Icon />}
      {children}
      {isPending && <Loader2 className="size-4 animate-spin" />}
    </button>
  );
  if (!disabledReason) return button;
  // A disabled button fires no pointer event: the wrapper carries the tooltip.
  return (
    <Tooltip text={disabledReason}>
      <span>{button}</span>
    </Tooltip>
  );
}
